import React from 'react';
import { APP_BTN_OUTLINE_CLASS, APP_BTN_PRIMARY_CLASS, APP_INPUT_CLASS, APP_TEXTAREA_CLASS } from '../../../ui/controlStyles';
import { OfferFloatingEditShell } from './OfferFloatingEditShell';
import { useOfferDocumentIssued } from './offerDocumentIssuedContext';

export type OfferInlineEditProps = {
  value: string;
  /** Wird bei „Speichern“ aufgerufen; bei Fehler Promise rejecten, damit der Edit-Modus offen bleibt. */
  onSave: (next: string) => void | Promise<void>;
  label?: React.ReactNode;
  multiline?: boolean;
  /**
   * Ersetzt den Text im Ansichtsmodus, wenn er anders aufgebaut ist als der bearbeitbare
   * Wert (z. B. Betreff: abgeleitetes Präfix und Titel als zwei umbrechende Boxen).
   */
  viewContent?: React.ReactNode;
  /** Klassen für den reinen Text im Ansichtsmodus (nicht den Wrapper). */
  viewTextClassName?: string;
  viewWrapperClassName?: string;
  editInputClassName?: string;
  emptyHint?: string;
  /** Replaces the default muted/italic empty-state classes. */
  emptyClassName?: string;
  'aria-label'?: string;
};

export const OfferInlineEdit: React.FC<OfferInlineEditProps> = ({
  value,
  onSave,
  label,
  multiline = true,
  viewContent,
  viewTextClassName = '',
  viewWrapperClassName = '',
  editInputClassName = '',
  emptyHint = 'Klicken zum Bearbeiten…',
  emptyClassName = 'text-neutral-400 italic',
  'aria-label': ariaLabel,
}) => {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value);
  const [saving, setSaving] = React.useState(false);
  const anchorRef = React.useRef<HTMLButtonElement>(null);
  const inputRef = React.useRef<HTMLTextAreaElement | HTMLInputElement>(null);

  React.useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  const openEdit = () => {
    setDraft(value);
    setEditing(true);
  };

  React.useEffect(() => {
    if (!editing) return;
    const t = requestAnimationFrame(() => {
      const el = inputRef.current;
      if (!el) return;
      el.focus();
      try {
        el.select();
      } catch {
        /* ignore */
      }
    });
    return () => cancelAnimationFrame(t);
  }, [editing]);

  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await Promise.resolve(onSave(draft));
      setEditing(false);
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };

  const issued = useOfferDocumentIssued();
  const displayText: React.ReactNode =
    viewContent != null ? viewContent : value.trim() ? value : issued ? '—' : emptyHint;
  const isEmptyDisplay = viewContent == null && !value.trim();
  const rows = multiline ? Math.min(14, Math.max(3, draft.split('\n').length + 1)) : 1;

  if (issued) {
    return (
      <span
        className={`whitespace-pre-wrap break-words block ${isEmptyDisplay ? 'text-neutral-400' : ''} ${viewTextClassName} ${viewWrapperClassName}`}
      >
        {displayText}
      </span>
    );
  }

  const field =
    multiline ? (
      <textarea
        ref={inputRef as React.RefObject<HTMLTextAreaElement>}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        rows={rows}
        disabled={saving}
        className={`${APP_TEXTAREA_CLASS} resize-y min-h-[4rem] leading-[1.45] ${editInputClassName}`}
        aria-label={ariaLabel}
      />
    ) : (
      <input
        ref={inputRef as React.RefObject<HTMLInputElement>}
        type="text"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={onKeyDown}
        disabled={saving}
        className={`${APP_INPUT_CLASS} ${editInputClassName}`}
        aria-label={ariaLabel}
      />
    );

  return (
    <>
      <button
        ref={anchorRef}
        type="button"
        onClick={openEdit}
        className={`w-full text-left rounded-md transition-colors hover:bg-app-hover/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-300/60 focus-visible:ring-offset-1 ${viewWrapperClassName}`}
        aria-label={ariaLabel ?? (typeof label === 'string' ? label : 'Text bearbeiten')}
      >
        <span
          className={`whitespace-pre-wrap break-words block ${isEmptyDisplay ? emptyClassName : ''} ${viewTextClassName}`}
        >
          {displayText}
        </span>
      </button>

      <OfferFloatingEditShell
        open={editing}
        onClose={cancel}
        anchorRef={anchorRef}
        panelWidth={multiline ? 360 : 280}
        ariaLabel={ariaLabel ?? (typeof label === 'string' ? label : 'Text bearbeiten')}
      >
        {label ? (
          <div className="mb-2 text-sm font-medium text-app-text">{label}</div>
        ) : null}
        {field}
        <div className="mt-3 flex justify-end gap-2 border-t border-app-border pt-3">
          <button type="button" onClick={cancel} disabled={saving} className={APP_BTN_OUTLINE_CLASS}>
            Abbrechen
          </button>
          <button type="button" onClick={() => void save()} disabled={saving} className={APP_BTN_PRIMARY_CLASS}>
            {saving ? 'Speichern…' : 'Speichern'}
          </button>
        </div>
      </OfferFloatingEditShell>
    </>
  );
};
